import {
    afterNextRender,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    linkedSignal,
    OnDestroy,
    OnInit,
    viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthController, CompanyController, DocumentController, DocumentTypeEnum } from '@domain';
import { environment } from '@environments';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaToastService } from '@shared/components';
import { RaTabItem, RaTabsComponent, uiPx } from 'ra-ui';
import { DATA_TAG_CONTEXT } from 'src/app/shared/components/ra-editor/data-tag-context.token';
import { OrderMainService } from 'src/app/modules/order/pages/main/services/order-main.service';
import { EditHeaderComponent } from './components/edit-header/edit-header.component';
import { EditModeRailComponent } from './components/edit-mode-rail/edit-mode-rail.component';
import { EditModeTransitionComponent } from './components/edit-mode-transition/edit-mode-transition.component';
import { AiChatPanelComponent } from './components/ai-chat-panel/ai-chat-panel.component';
import { AiEditPreviewIslandComponent } from './components/ai-chat-panel/ai-edit-preview-island/ai-edit-preview-island.component';
import { AiEditPreviewNoteComponent } from './components/ai-chat-panel/ai-edit-preview-note/ai-edit-preview-note.component';
import { PdfPreviewPanelComponent } from './components/pdf-preview-panel/pdf-preview-panel.component';
import { MediaLookPanelComponent } from './components/media-look-panel/media-look-panel.component';
import { QuoteEditorComponent } from './components/quote/quote-editor.component';
import { SectionItemsComponent } from './components/section-items/section-items.component';
import { SectionsListComponent } from './components/sections-list/sections-list.component';
import { EditDataService } from './services/_edit-data.service';
import { MediaLookPanelService } from './services/media-look-panel.service';
import { EditService } from './services/edit-service';
import { EditModeService } from './services/edit-mode.service';
import { AiEditStore } from './services/ai-edit.store';
import { FieldRemovalService } from './services/field-removal.service';
import { TextEditingService } from './services/text-editing.service';
import { TemplateEditService } from './services/template-edit.service';
import { I18nService } from 'src/app/shared/services/i18n.service';

@Component({
    selector: 'edit-page',
    templateUrl: 'edit.page.html',
    styleUrl: 'edit.page.scss',
    imports: [
        EditHeaderComponent,
        EditModeRailComponent,
        EditModeTransitionComponent,
        AiChatPanelComponent,
        AiEditPreviewIslandComponent,
        AiEditPreviewNoteComponent,
        SectionsListComponent,
        SectionItemsComponent,
        QuoteEditorComponent,
        PdfPreviewPanelComponent,
        MediaLookPanelComponent,
        RaTabsComponent,
        TranslatePipe,
    ],
    providers: [
        {
            provide: DATA_TAG_CONTEXT,
            useFactory: () => {
                const editData = inject(EditDataService);
                const companyController = inject(CompanyController);
                // Garante empresa do cookie no signal antes do primeiro computed.
                companyController.getCurrentCompany();
                return computed(() => ({
                    document: editData.document(),
                    client: editData.client(),
                    company: companyController.currentCompany(),
                }));
            },
        },
    ],
})
export class EditPage implements OnInit, OnDestroy {
    // * [Injects]
    public editService = inject(EditService);
    private _router = inject(Router);
    private _destroyRef = inject(DestroyRef);
    private _toastService = inject(RaToastService);
    private _translate = inject(TranslateService);
    private _i18n = inject(I18nService);
    private _activateRoute = inject(ActivatedRoute);
    private _editDataService = inject(EditDataService);
    private _documentController = inject(DocumentController);
    private _authController = inject(AuthController);
    private _orderMainService = inject(OrderMainService);
    private _textEditing = inject(TextEditingService);
    private _fieldRemovalService = inject(FieldRemovalService);
    private _templateEditService = inject(TemplateEditService);
    private _host = inject(ElementRef<HTMLElement>);
    private _editModeService = inject(EditModeService);
    private _aiEditStore = inject(AiEditStore);

    // * [View children]
    // `read: ElementRef` é obrigatório: numa tag de componente o viewChild devolve a
    // instância, e sem isto o `nativeElement` era undefined, a medição nunca rodava e o
    // layout vivia do fallback do SCSS (que só batia com o cabeçalho de uma linha).
    private readonly _editHeader = viewChild('editHeader', { read: ElementRef<HTMLElement> });

    // * [State]
    public sessionsCollapsed = this._editDataService.sessionsCollapsed;
    public pdfPreviewOpen = this._editDataService.pdfPreviewOpen;
    public pdfPreviewExpanded = this._editDataService.pdfPreviewExpanded;
    /** O painel de formato da mídia é dono da coluna da direita (na largura dele). */
    /** Painel da mídia aberto: a prévia sobe acima do fundo escuro dele. */
    public mediaFocus = inject(MediaLookPanelService).active;

    // * [Modos] Manual × "Editar com IA" (docs/EDITOR-CHAT-IA.md). Sem a flag
    // `ai_edit_mode` liberada não há rail e o editor fica como sempre foi.
    public showRail = this._editModeService.featureEnabled;
    public isAiMode = this._editModeService.isAiMode;

    // * [Quote] Flag de tipo (só para documentos do tipo QUOTE)
    public isQuote = computed(
        () => this._editDataService.document()?.type === DocumentTypeEnum.QUOTE
    );

    /**
     * Espaçamento entre linhas do documento, aplicado como var CSS no container
     * (cards e preview leem `--line-spacing`). Ausente => 1.5, o mesmo default
     * do PdfEngine (padrão de laudo, ABNT NBR 14724).
     */
    public lineSpacing = computed(
        () => this._editDataService.document()?.config?.lineSpacing ?? 1.5
    );

    /**
     * Tamanho do texto normal (config em pt; 12 = padrão ABNT) convertido para
     * rem (pt × 4/3 px de desenho ÷ 16 = pt ÷ 12), aplicado como var CSS
     * `--text-size` no container: 12pt = 1rem, e o card acompanha a escala da UI
     * como o resto do editor. Só o item TEXT: título e subtítulo têm tamanho
     * fixo nos cards.
     */
    public textSize = computed(() => {
        const pt = this._editDataService.document()?.config?.fontSize ?? 12;
        return `${Math.round((pt / 12) * 10000) / 10000}rem`;
    });

    /** Alinhamento do texto (config textAlign), var CSS `--text-align`: justificado é o padrão do PDF. */
    public textAlign = computed(() =>
        this._editDataService.document()?.config?.textAlign === 'left' ? 'left' : 'justify'
    );

    // * [Quote] Abas em pílula (mesmo `ra-tabs` do feed). Os rótulos são resolvidos
    // aqui porque o DS não traduz; o `t` do I18nService mantém a lista reativa.
    public quoteTabs = computed<RaTabItem[]>(() => {
        return [
            { value: 'quote', label: this._i18n.t('document.edit.quote.tab') },
            { value: 'content', label: this._i18n.t('document.edit.quote.contentTab') },
        ];
    });

    // * [Quote] Aba ativa. Abre direto na aba do orçamento assim que o documento
    // carrega como QUOTE; depois respeita a troca manual do usuário (linkedSignal
    // só recomputa quando `isQuote` muda, e ele muda uma única vez, no load).
    public quoteTab = linkedSignal<boolean, 'content' | 'quote'>({
        source: this.isQuote,
        computation: (isQuote, previous) => {
            // Primeira vez que o documento vira orçamento → abre na aba do orçamento.
            if (isQuote && !previous?.source) return 'quote';
            // Mantém a aba anterior (inclusive a escolha manual do usuário).
            return previous?.value ?? 'content';
        },
    });

    // * [Properties]
    private _beforeUnloadHandler?: (event: BeforeUnloadEvent) => void | string;
    private _hasRemovedUser = false;
    private _headerResizeObserver?: ResizeObserver;
    private _restoreToastVariant?: () => void;

    constructor() {
        // Na aba do ORÇAMENTO a lista de seções fica fechada e sem toggle: as seções
        // dali são as do CONTEÚDO, e deixá-las abertas enquanto o usuário mexe em
        // itens/taxas convida a editar a coisa errada. Volta ao normal em "Conteúdo".
        effect(() => {
            this._editDataService.setSessionsLocked(this.quoteTab() === 'quote');
        });

        // Âncoras das ilhas globais (toast pill e pilha de upload), publicadas no
        // <body> porque os componentes são globais e não enxergam o --sidebar-width
        // local. O recuo alinha o centro delas com o da barra de seleção em lote; o
        // lift as mantém acima desse slot (bottom 36 + ~54 de altura) SÓ enquanto a
        // barra existe: sem seleção (e sem o composer de rodapé, que saiu com a
        // edição inline) elas descem ao pé da tela.
        // Medidas de desenho em rem (16 = 1rem), como no SCSS do editor.
        effect(() => {
            // O rail de modos soma a sua coluna; no modo IA não há lista de seções.
            const rail = this.showRail() ? 4.5 : 0;
            const sidebar = this.isAiMode() ? 0 : this.sessionsCollapsed() ? 7 : 25;
            document.body.style.setProperty('--island-inset-left', `${rail + sidebar}rem`);
            document.body.style.setProperty(
                '--island-lift',
                this._editDataService.selectedItems().length > 0 ? '4.625rem' : '0px'
            );
        });

        // No editor todo retorno flutuante é ilha escura no rodapé (save, upload,
        // desfazer). O card branco no canto esquerdo destoava dessa linguagem, então
        // aqui os toasts sem variante explícita nascem como pill. Restaurado no destroy.
        this._restoreToastVariant = this._toastService.setDefaultVariant('pill');

        // Mede a altura do header flutuante e expõe em --edit-header-height,
        // para o conteúdo começar abaixo dele e rolar por trás.
        afterNextRender(() => {
            const header = this._editHeader()?.nativeElement;
            if (!header) return;

            this._updateHeaderHeight();
            this._headerResizeObserver = new ResizeObserver(() => this._updateHeaderHeight());
            this._headerResizeObserver.observe(header);
        });
    }

    ngOnInit(): void {
        this._initialize();
        this._setupBeforeUnloadListener();
    }

    private _updateHeaderHeight(): void {
        const header = this._editHeader()?.nativeElement;
        if (!header) return;

        // offsetHeight (sem margens) + margem-topo (12) + gap (28), em medida de desenho. O
        // gap cresceu com a barra de formatação: com 12 a folha nascia colada nela.
        const clearance = header.offsetHeight + uiPx(40);
        (this._host.nativeElement as HTMLElement).style.setProperty(
            '--edit-header-height',
            `${clearance}px`
        );
    }

    ngOnDestroy(): void {
        // Fora do editor as ilhas voltam ao rodapé inteiro da viewport.
        document.body.style.removeProperty('--island-inset-left');
        document.body.style.removeProperty('--island-lift');

        // Fora do editor o toast volta a ser o card padrão do app.
        this._restoreToastVariant?.();

        // Sair do editor confirma imediatamente uma remoção pendente de desfazer
        // (o timer morreria junto com a tela e o campo "voltaria" ao reabrir).
        this._fieldRemovalService.flush().subscribe();

        // Remove o listener do beforeunload
        this._removeBeforeUnloadListener();

        // Para de observar o header
        this._headerResizeObserver?.disconnect();

        // Ao voltar para a lista do serviço, rebusca os documentos para refletir
        // tamanho e metadados recalculados pelo backend durante a edição.
        this._requestOrderDocumentsReload();

        // Remove o usuário online quando o componente é destruído (navegação normal)
        if (!this._hasRemovedUser && !this._editDataService.documentDeleted()) {
            this._removeOnlineUser();
        }

        // Limpa o documento em cache
        this._editDataService.clearDocument();

        // O próximo documento resolve o modo de novo (URL, último usado, tipo do documento).
        this._editModeService.clear();
        this._aiEditStore.clear();

        // Estado transitório da tela: os serviços do editor são `providedIn: 'root'`
        // (sobrevivem à navegação), então sair sem limpar fazia o editor reabrir com o
        // campo da visita anterior ainda em edição e ativo na barra, e com itens ainda
        // marcados na seleção.
        this._textEditing.clear();
        this._editDataService.cleanSelectedItems();

        // Modo modelo: quem sai pela seta já sincronizou (ver `EditHeaderComponent.backPage`);
        // isto cobre qualquer outra saída (menu lateral, link, histórico do navegador).
        if (this._templateEditService.isTemplateMode()) {
            this._templateEditService.syncOnLeave().subscribe();
        }
        this._templateEditService.clear();
    }

    private _requestOrderDocumentsReload(): void {
        const document = this._editDataService.document();
        if (!document?.orderId || this._editDataService.documentDeleted()) return;

        this._orderMainService.requestDocumentsReload();
    }

    /**
     * Busca o ID do documento na URL, e inicializa o serviço de edição
     */
    private _initialize(): void {
        // `?templateId=` liga o MODO MODELO: o documento aberto é o rascunho de edição de um
        // modelo, criado pela API. O nome do modelo pode vir no state de quem navegou (a lista
        // já tem) — no refresh da página o serviço o busca sozinho.
        const templateId = this._activateRoute.snapshot.queryParams['templateId'];
        if (templateId) {
            const templateName = history.state?.templateName as string | undefined;
            this._templateEditService.start(templateId, templateName);
        } else {
            this._templateEditService.clear();
        }

        const id = this._activateRoute.snapshot.params['id'];
        if (!id) {
            this._router.navigate(['/platform']);
            this._toastService.error({
                message: this._translate.instant('document.edit.page.documentIdNotFound'),
            });
            return;
        }

        this._editModeService.init(this._activateRoute);
        this.editService.initialize(id, this._destroyRef);
    }

    /**
     * Configura o listener para o evento beforeunload
     * Garante que a request seja executada mesmo quando o usuário fecha a aba
     * E mostra alerta se houver alterações não salvas
     */
    private _setupBeforeUnloadListener(): void {
        this._beforeUnloadHandler = (event: BeforeUnloadEvent) => {
            // Fechar a aba mata o timer da janela de desfazer: envia a remoção
            // pendente agora, com `keepalive` (a request sobrevive à página).
            this._fieldRemovalService.commitPendingWithKeepalive();

            // Verifica se há alterações não salvas
            if (!this._hasRemovedUser) {
                this._removeOnlineUserWithBeacon();
            }

            // Modo modelo: leva a última edição para o modelo antes de a aba morrer
            this._templateEditService.syncWithKeepalive();
        };

        window.addEventListener('beforeunload', this._beforeUnloadHandler);
    }

    /**
     * Remove o listener do beforeunload
     */
    private _removeBeforeUnloadListener(): void {
        if (this._beforeUnloadHandler) {
            window.removeEventListener('beforeunload', this._beforeUnloadHandler);
            this._beforeUnloadHandler = undefined;
        }
    }

    /**
     * Remove o usuário online usando o método normal (para navegação dentro da aplicação)
     */
    private _removeOnlineUser(): void {
        const documentId = this._editDataService.document()?._id;
        if (!documentId) return;

        this._hasRemovedUser = true;
        this._documentController.removeOnlineUser(documentId).subscribe();
    }

    /**
     * Remove o usuário online usando fetch com keepalive (para quando a aba é fechada)
     * Usa fetch com keepalive: true para garantir que a requisição seja enviada mesmo
     * quando a página está sendo descarregada
     */
    private _removeOnlineUserWithBeacon(): void {
        const documentId = this._editDataService.getDocumentId();
        if (!documentId) return;

        this._hasRemovedUser = true;

        const token = this._authController.getTokenInCookie();
        const url = `${environment.API_BASE}document/remove-online-user/${documentId}`;

        // Usa fetch com keepalive: true para garantir que a requisição seja enviada
        // mesmo quando a página está sendo descarregada
        fetch(url, {
            method: 'PATCH',
            headers: {
                ...(token && { Authorization: token }),
                'Content-Type': 'application/json',
            },
            keepalive: true, // Garante que a requisição seja enviada mesmo após o descarregamento da página
        }).catch(() => {
            // Silenciosamente ignora erros, pois a página pode já estar sendo descarregada
        });
    }
}
